import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { AssertionScoreErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { FormattedRpServiceConfigurationScoreModifier } from "@idw-types/data/rp-service-configuration/rp-service-configuration-score";
import { ScoreReferenceData } from "@idw-types/data/rp-service-configuration/scores/score-reference-data";
import { getAssertionScoreModifierPoints } from "@utils/scores/assertion-score-modifier-util";

import {
    isAssertionScoreEnabled,
    updateServiceOfferingsAndAssertionScoreModifiers
} from "@utils/scores/score-util";
import { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import commonStyles from "../../verification-service-details-component.less";
import { AssertionScoreModifiersTableContainer } from "./assertion-score-modifiers-table-container/assertion-score-modifiers-table-container";
import styles from "./assertion-score-tab-container.less";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type AssertionScoreTabContainerProps = {
    isDisabled: boolean;
    validationErrors: AssertionScoreErrors;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    assertionScoreServiceOfferingIDs: Array<string>;
    scoreServiceOfferingIDs: Array<string>;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    scoreReferenceData: ScoreReferenceData;
    setRpServiceConfigurationState: (
        state: FormattedRpServiceConfiguration
    ) => void;
};

export const AssertionScoreTabContainer = (
    props: AssertionScoreTabContainerProps
) => {
    const {
        isDisabled,
        validationErrors,
        rpServiceConfigurationState,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        setRpServiceConfigurationState,
        scoreReferenceData
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);

    const shouldEnableAssertionScoreToggle = useMemo(() => {
        return isAssertionScoreEnabled(
            rpServiceConfigurationState.serviceOfferings,
            assertionScoreServiceOfferingIDs
        );
    }, [rpServiceConfigurationState.serviceOfferings]);

    const assertionScoreModifierPoints = useMemo(() => {
        return getAssertionScoreModifierPoints(
            rpServiceConfigurationState.assertionScoreModifiers
        );
    }, [rpServiceConfigurationState.assertionScoreModifiers]);

    const handleShowCanViewAssertionScoreModifiersChange = () => {
        const {
            serviceOfferings,
            assertionScoreModifiers
        } = updateServiceOfferingsAndAssertionScoreModifiers(
            rpServiceConfigurationState,
            assertionStatusMaps,
            assertionScoreServiceOfferingIDs,
            scoreServiceOfferingIDs,
            !shouldEnableAssertionScoreToggle
        );

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            serviceOfferings,
            assertionScoreModifiers
        });
    };

    const setAssertionScoreModifiersState = (
        assertionScoreModifiers: Array<
            FormattedRpServiceConfigurationScoreModifier
        >
    ) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            assertionScoreModifiers
        });
    };

    return (
        <div>
            <ContentPageHeader
                title={
                    resources.pages.verificationServiceDetails.tabs.scores.subTabs
                        .assertionScore.title
                }
                headerButton={
                    <ToggleFormGroup
                        className={styles.assertionScoreToggleForm}
                        controlId={"enable-assertion-score-toggle"}
                        automationId={"enable-assertion-score-toggle"}
                        label={"Enable Assertion Score"}
                        ariaLabel={"Enable Assertion Score"}
                        checkedIcon={false}
                        uncheckedIcon={false}
                        labelWidth={labelWidth}
                        checked={shouldEnableAssertionScoreToggle}
                        disabled={isDisabled}
                        isRequired={true}
                        onChange={handleShowCanViewAssertionScoreModifiersChange}
                        value={`${shouldEnableAssertionScoreToggle}`}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true}>
                <div>
                    <span className={commonStyles.description}>
                        {
                            resources.pages.verificationServiceDetails.tabs.scores.subTabs
                                .assertionScore.description
                        }
                    </span>

                    {shouldEnableAssertionScoreToggle && (
                        <>
                            <div className={styles.assertionScoreModifiersSection}>
                                <h1>
                                    {
                                        resources.pages.verificationServiceDetails.tabs.scores
                                            .subTabs.assertionScore.sections.assertionScoreModifiers
                                            .title
                                    }
                                </h1>

                                <h1 className={styles.maximumPointsText}>
                                    {`${resources.pages.verificationServiceDetails.tabs.scores.subTabs.assertionScore.sections.assertionScoreModifiers.maximumPossibleAssertionLabel}: ${assertionScoreModifierPoints}`}
                                </h1>
                            </div>

                            {rpServiceConfigurationState.assertionScoreModifiers.length ===
                                0 && (
                                    <Alert variant={"info"}>
                                        {
                                            resources.pages.verificationServiceDetails.tabs.scores
                                                .subTabs.assertionScore.sections.assertionScoreModifiers
                                                .noAssertionScoreModifierLabel
                                        }
                                    </Alert>
                                )}

                            {rpServiceConfigurationState.assertionScoreModifiers.length > 0 &&
                                validationErrors.noAssertionScoreValueSetError && (
                                    <div className={styles.assertionScoreError}>
                                        {
                                           validationErrors.noAssertionScoreValueSetError
                                        }
                                    </div>
                                )}

                            {rpServiceConfigurationState.assertionScoreModifiers.length >
                                0 && (
                                    <AssertionScoreModifiersTableContainer
                                    scoreReferenceData={scoreReferenceData}
                                        isDisabled={isDisabled}
                                        assertionScoreModifiers={
                                            rpServiceConfigurationState.assertionScoreModifiers
                                        }
                                        canViewPassAssertionScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewPassAssertionScoreModifiers
                                        }
                                        canViewCloseAssertionScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewCloseAssertionScoreModifiers
                                        }
                                        canViewPartialAssertionScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewPartialAssertionScoreModifiers
                                        }
                                        canViewUnverifiedAssertionScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewUnverifiedAssertionScoreModifiers
                                        }
                                        canViewFailAssertionScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewFailAssertionScoreModifiers
                                        }
                                        formattedServiceOfferings={
                                            rpServiceConfigurationState.serviceOfferings
                                        }
                                        setAssertionScoreModifiersState={
                                            setAssertionScoreModifiersState
                                        }
                                    />
                                )}
                        </>
                    )}
                </div>
            </ContentPageBody>
        </div>
    );
};
